import React, { useState } from 'react'
import DatePicker from 'react-widgets/DatePicker'
import DropdownList from 'react-widgets/DropdownList'
import { DateLocalizer } from 'react-widgets/IntlLocalizer'
import Localization from 'react-widgets/Localization'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import 'react-widgets/styles.css'
import { generateDate } from 'client/components/core/Sidebar/components/Settings/entities/Countdown/utils/date-time-utils'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { DatePickerUi } from './ui/date-picker-ui'
import { DropdownListUi } from './ui/dropdown-list-ui'
import { getDefaultTime, timeOptions } from './utils'

interface DateTimePickerProps {
  dateTime: Date
  updateDate: (date: Date) => void
  showDate?: boolean
}

const DateTimePicker = ({
  updateDate,
  dateTime,
  showDate = true,
}: DateTimePickerProps) => {
  const { i18n } = useTypedTranslation()
  const fallbackLng = i18n.options.fallbackLng
    ? (i18n.options.fallbackLng as string[])[0]
    : 'en'
  const language = i18n.language || fallbackLng

  const [time, setTime] = useState<string>(getDefaultTime(dateTime, language))
  const [date, setDate] = useState<Date>(dateTime)
  const [currentCalendarDate, setCurrentCalendarDate] = useState(dateTime)
  return (
    <OptionWrapper>
      <Localization
        date={
          new DateLocalizer({
            culture: language,
            firstOfWeek: 1,
          })
        }
      >
        <div className="flex gap-3">
          {showDate && (
            <DatePickerUi>
              <DatePicker
                containerClassName="[&>.rw-input]:h-10!"
                lang={language}
                currentDate={currentCalendarDate}
                calendarProps={{
                  view: 'month',
                  formats: {
                    weekday: (date: Date) => {
                      const short = date.toLocaleDateString(language, {
                        weekday: 'short',
                      })

                      return short.slice(0, 2)
                    },
                  },
                  renderDay: ({ label, ...props }) => {
                    const isToday =
                      props.date.toDateString() === new Date().toDateString() &&
                      props.date.toDateString() !==
                        new Date(date).toDateString()

                    return (
                      <span
                        {...props}
                        className={
                          isToday ? 'text-[var(--color-primary-500)]' : ''
                        }
                      >
                        {label}
                      </span>
                    )
                  },
                  onNavigate: date => {
                    setCurrentCalendarDate(date)
                  },
                }}
                onToggle={isOpen => {
                  if (isOpen) {
                    setCurrentCalendarDate(date)
                  }
                }}
                defaultValue={dateTime}
                onChange={date => {
                  if (date) {
                    setDate(date)
                    updateDate(generateDate({ date, time }))
                    setCurrentCalendarDate(date)
                  }
                }}
              />
            </DatePickerUi>
          )}
          <DropdownListUi showDate={showDate}>
            <DropdownList
              className="h-full"
              containerClassName="h-full"
              data={timeOptions(language)}
              onChange={time => {
                setTime(time)
                updateDate(generateDate({ date, time }))
              }}
              defaultValue={time}
            />
          </DropdownListUi>
        </div>
      </Localization>
    </OptionWrapper>
  )
}
export default DateTimePicker
